import { useSuspenseQuery } from '@tanstack/react-query';
import {
  SourcingService,
  type IndeedIntegrationSettings,
  type VonqContracts,
} from 'api-clients/monolith';

import { useIndeedSettings } from '@/hooks/use-indeed-settings';
import { queryKeys } from '@/query-keys';

export const POPULAR_CONTRACTS = ['indeed', 'linkedin'] as const;

export const vonqContractsQueryOptions = {
  queryKey: queryKeys.settings.contracts.vonqContracts,
  queryFn: () => SourcingService.getInternalApiSourcingVonqContracts(),
};

export function isPopularIndeedVisible(
  settings: Pick<IndeedIntegrationSettings, 'indeed_authorized'> | undefined,
  locked: boolean,
) {
  return locked || !settings?.indeed_authorized;
}

export function hasLinkedInContract(
  contracts: VonqContracts['contracts'] | undefined,
) {
  return (
    contracts?.some(contract => contract.name.toLowerCase() === 'linkedin') ??
    false
  );
}

export function usePopularContracts() {
  const { data: settings } = useIndeedSettings();
  const linkedin = usePopularLinkedInContract();
  return [
    ...(!settings.indeed_authorized ? [{ id: undefined, name: 'Indeed' }] : []),
    ...(linkedin ? [linkedin] : []),
  ];
}

// LinkedIn must remain available when the independent Indeed settings read fails.
export function usePopularLinkedInContract() {
  const {
    data: { contracts },
  } = useSuspenseQuery(vonqContractsQueryOptions);
  const { data } = useSuspenseQuery({
    queryKey: [...queryKeys.settings.contracts.vonqChannels, 'LinkedIn'],
    queryFn: () =>
      SourcingService.getInternalApiSourcingVonqContractsChannels('LinkedIn'),
  });
  const linkedin = data.channels.find(
    channel => channel.name.toLowerCase() === 'linkedin',
  );
  if (hasLinkedInContract(contracts)) return null;
  return { id: linkedin?.id, name: linkedin?.name ?? 'LinkedIn' };
}
